import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useS3UploadAsync } from "@src/features/app/hooks/useS3Upload";
import { gql } from "@apollo/client";
import { useUploadFileCreateMediaMutation } from "./useUploadFileAndExtractMarkdown.generated";
import { MediaFragment } from "@src/fragments.generated";

export interface FileUpload {
  uuid: string;
  state: "uploading" | "success" | "error";
  fileName: string;
  file?: File;
  media?: MediaFragment;
  error?: string;
}

export interface UseUploadFileAndExtractMarkdown {
  uploadFiles: (files: File[]) => void;
}

export function useUploadFileAndExtractMarkdown({
  onUploadStarted,
  onUploadCompleted,
  onUploadError,
}: {
  onUploadStarted: (fileUpload: FileUpload) => void;
  onUploadCompleted: (fileUpload: FileUpload) => void;
  onUploadError: (fileUpload: FileUpload) => void;
}): UseUploadFileAndExtractMarkdown {
  const [createMedia] = useUploadFileCreateMediaMutation();

  const { handleFileUpload } = useS3UploadAsync();

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!files || files.length === 0) return;

      const uploadPromises = files.map(async (file) => {
        const uuid = uuidv4();
        const uploadingFile: FileUpload = {
          uuid,
          state: "uploading",
          fileName: file.name,
          file: file,
        };

        onUploadStarted(uploadingFile);

        try {
          const upload = await handleFileUpload(file, uuid);
          if (!upload) {
            const errorFile: FileUpload = {
              ...uploadingFile,
              file: undefined,
              state: "error",
              error: "Upload failed",
            };
            onUploadError(errorFile);
            return;
          }

          const { data } = await createMedia({
            variables: {
              input: {
                mediaUrls: { en: upload?.url },
              },
            },
          });

          if (!data?.createMedia?.success || !data.createMedia.media) {
            const errorFile: FileUpload = {
              ...uploadingFile,
              file: undefined,
              state: "error",
              error: data?.createMedia?.error?.code || "Media creation failed",
            };
            onUploadError(errorFile);
            return;
          }

          onUploadCompleted({
            ...uploadingFile,
            media: data.createMedia.media,
            state: "success",
          });
        } catch (error) {
          onUploadError({
            ...uploadingFile,
            state: "error",
            error: error instanceof Error ? error.message : "Upload failed",
          });
        }
      });

      await Promise.allSettled(uploadPromises);
    },
    [
      createMedia,
      onUploadCompleted,
      onUploadError,
      onUploadStarted,
      handleFileUpload,
    ],
  );

  return {
    uploadFiles,
  };
}

gql`
  mutation UploadFileCreateMedia($input: MediaInput!) {
    createMedia(input: $input) {
      success
      error {
        code
      }
      media {
        ...Media
      }
    }
  }
`;
